import { useEffect, useState } from "react";
import { X, Play, Trash2, Film, Loader2, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { apiViewer, fileUrl, formatApiError } from "@/lib/api";

export default function MyWatchlistModal({ onClose, onTrailer, onChanged }) {
  const [items, setItems] = useState(null);

  const load = () =>
    apiViewer.get("/watchlist").then((r) => setItems(r.data.shows)).catch(() => toast.error("Could not load your watchlist"));

  useEffect(() => {
    load();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const remove = (show) => {
    apiViewer
      .post("/watchlist/toggle", { slug: show.slug })
      .then(({ data }) => {
        setItems((prev) => (prev || []).filter((s) => s.slug !== show.slug));
        onChanged(new Set(data.slugs));
        toast(`${show.title} removed from your Watchlist`);
      })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  };

  return (
    <div data-testid="watchlist-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" data-testid="watchlist-modal-backdrop" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gold-500/30 bg-[#0c0a07] shadow-[0_0_100px_rgba(212,175,55,0.15)]">
        <div className="flex items-center justify-between border-b border-gold-500/15 px-6 py-4">
          <div>
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gold-500">
              <Bookmark className="h-3.5 w-3.5" /> Saved by you
            </p>
            <h2 className="mt-1 font-display text-xl font-bold text-stone-50">My Watchlist</h2>
          </div>
          <button
            data-testid="watchlist-modal-close-button"
            aria-label="Close watchlist"
            onClick={onClose}
            className="rounded-full border border-stone-700 p-2.5 text-stone-300 transition-colors hover:border-gold-500/60 hover:text-gold-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gold-500" /></div>
          ) : items.length === 0 ? (
            <div data-testid="watchlist-empty-state" className="flex flex-col items-center gap-4 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold-500/30 bg-gold-500/5 text-gold-400">
                <Film className="h-7 w-7" />
              </span>
              <p className="font-display text-lg font-bold uppercase tracking-[0.25em] text-gold-300">Your watchlist is empty</p>
              <p className="max-w-sm text-sm text-stone-500">Explore VIEWNA shows and tap “Add to Watchlist” to save them here.</p>
              <button
                data-testid="watchlist-explore-button"
                onClick={onClose}
                className="mt-2 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-7 py-3 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.4)]"
              >
                Explore Shows
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5">
              {items.map((show) => (
                <div
                  key={show.slug}
                  data-testid={`watchlist-item-${show.slug}`}
                  className="group relative overflow-hidden rounded-xl border border-stone-800 bg-ink-900 transition-all duration-300 hover:border-gold-500/40"
                >
                  <div className="relative aspect-[2/3]">
                    {show.poster_url ? (
                      <img src={fileUrl(show.poster_url)} alt={show.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-ink-800 text-gold-500/50"><Film className="h-8 w-8" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950/90 via-transparent to-transparent" />
                    <button
                      data-testid={`watchlist-remove-${show.slug}`}
                      aria-label={`Remove ${show.title} from watchlist`}
                      onClick={() => remove(show)}
                      className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-stone-600/70 bg-ink-950/80 text-stone-300 backdrop-blur transition-all duration-300 hover:border-red-500/70 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`watchlist-play-${show.slug}`}
                      aria-label={`Play preview of ${show.title}`}
                      onClick={() => onTrailer(show)}
                      className="absolute inset-0 z-10 flex items-center justify-center opacity-100 transition-all duration-300 lg:opacity-0 lg:group-hover:opacity-100"
                    >
                      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold-400/70 bg-ink-950/80 text-gold-300 transition-all duration-300 hover:scale-110 hover:bg-gold-500 hover:text-ink-950">
                        <Play className="h-5 w-5 fill-current" />
                      </span>
                    </button>
                    <div className="absolute inset-x-0 bottom-0 p-3">
                      <p className="font-display text-sm font-bold text-stone-50">{show.title}</p>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-stone-400">{show.category}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
